import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import type { Task, Client, Project } from '@/types';
import { clientsService } from '@/services/clients.service';
import { projectsService } from '@/services/projects.service';
import { tasksService } from '@/services/tasks.service';

// Origin indicator component for task detail view
function TaskOriginIndicator({ task }: { task: Task }) {
  if (task.origin === 'flowky' || !task.origin) {
    return null;
  }

  const originConfig = {
    github: {
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
      ),
      label: 'GitHub Issue',
      className: 'bg-gray-800 text-white',
    },
  };

  const config = originConfig[task.origin as keyof typeof originConfig];
  if (!config) return null;

  const badge = (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.className}`}
    >
      {config.icon}
      <span>{config.label}</span>
    </span>
  );

  // If there's an external URL, make it clickable
  if (task.externalUrl) {
    return (
      <a
        href={task.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80 transition-opacity"
        title={task.externalData?.repoFullName ? `View on ${config.label.replace(' Issue', '')}: ${task.externalData.repoFullName}#${task.externalData.issueNumber}` : `View on ${config.label.replace(' Issue', '')}`}
      >
        {badge}
      </a>
    );
  }

  return badge;
}

interface TaskDetailViewProps {
  task: Task;
}

export function TaskDetailView({ task }: TaskDetailViewProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(task.status);
  const [client, setClient] = useState<Client | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [comments, setComments] = useState(task.comments || []);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    loadClientAndProject();
  }, [task.clientId, task.projectId]);

  const loadClientAndProject = async () => {
    try {
      setLoading(true);
      const promises = [];

      if (task.clientId) {
        promises.push(clientsService.getClient(task.clientId));
      }
      if (task.projectId) {
        promises.push(projectsService.getProject(task.projectId));
      }

      const results = await Promise.all(promises);

      if (task.clientId) {
        setClient(results[0] as Client);
      }
      if (task.projectId) {
        setProject(results[task.clientId ? 1 : 0] as Project);
      }
    } catch (error) {
      console.error('Error loading client/project:', error);
    } finally {
      setLoading(false);
    }
  };

  const priorityStyles = {
    high: 'border-red-500 text-red-500',
    medium: 'border-muted-foreground text-muted-foreground',
    low: 'border-green-600 text-green-600',
  };

  const statusStyles = {
    'not-started': 'bg-gray-100 text-gray-700',
    'in-progress': 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
  };

  const handleStatusChange = async (newStatus: typeof status) => {
    try {
      setUpdating(true);
      await tasksService.updateTask(task.id, { status: newStatus });
      setStatus(newStatus);
    } catch (error) {
      console.error('Error updating task status:', error);
      // Revert to previous status on error
      setStatus(task.status);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      setDeleting(true);
      await tasksService.deleteTask(task.id);
      navigate('/tasks');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setAddingComment(true);
      const updatedTask = await tasksService.addComment(task.id, newComment);
      setComments(updatedTask.comments || []);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      const updatedTask = await tasksService.deleteComment(task.id, commentId);
      setComments(updatedTask.comments || []);
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && status !== 'completed';

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header Card */}
      <div className="border border-border rounded-lg p-4 mb-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{task.title}</h1>

            {/* Badges Row */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <TaskOriginIndicator task={task} />
              <span className={`px-3 py-1 text-sm rounded-full border font-medium ${priorityStyles[task.priority]}`}>
                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyles[status]}`}>
                {status === 'not-started' ? 'Not Started' : status === 'in-progress' ? 'In Progress' : 'Completed'}
              </span>
              {isOverdue && (
                <span className="px-3 py-1 text-sm rounded-full bg-red-100 text-red-700 font-medium">
                  Overdue
                </span>
              )}
            </div>

            {/* Metadata */}
            <div className="flex items-center gap-6 text-sm text-gray-600">
              {loading ? (
                <span className="text-gray-400">Loading...</span>
              ) : (
                <>
                  {client && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-medium">{client.name}</span>
                    </div>
                  )}
                  {project && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      <span className="font-medium">{project.name}</span>
                    </div>
                  )}
                  {task.deadline && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                        Due {new Date(task.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value as typeof status)}
              disabled={updating || deleting}
              className={`px-4 py-2 text-sm rounded-lg border-0 font-medium ${statusStyles[status]} disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
            >
              <option value="not-started">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
            <Link
              to={`/tasks/${task.id}/edit`}
              className={`px-4 py-2 bg-white/60 border border-white/30 rounded-lg hover:bg-white/80 transition-colors font-medium ${deleting ? 'pointer-events-none opacity-50' : ''}`}
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              disabled={deleting || updating}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              {deleting && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Description and Comments */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="border border-border rounded-lg p-4">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              Description
            </h2>
            {task.description ? (
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{task.description}</p>
            ) : (
              <p className="text-gray-500 italic">No description provided</p>
            )}
          </div>

          {/* Comments */}
          <div className="border border-border rounded-lg p-4">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Comments {comments.length > 0 && `(${comments.length})`}
            </h2>

            {/* Add Comment Form */}
            <div className="mb-4">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-flowky-primary focus:border-transparent resize-none"
                disabled={addingComment}
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || addingComment}
                  className="px-4 py-2 bg-flowky-primary text-white text-sm rounded-lg hover:bg-flowky-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingComment ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            </div>

            {/* Comments List */}
            <div className="space-y-3">
              {comments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No comments yet</p>
              ) : (
                comments
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((comment) => (
                    <div key={comment.id} className="p-4 bg-white/40 rounded-lg border border-gray-200/50">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="text-xs text-gray-500">
                          {new Date(comment.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-red-600 hover:text-red-700 text-xs font-medium"
                        >
                          Delete
                        </button>
                      </div>
                      <p className="text-gray-800 whitespace-pre-wrap">{comment.text}</p>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Task Details */}
        <div className="border border-border rounded-lg p-4 h-fit">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Details
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-200/50">
              <span className="text-sm text-gray-600 font-medium">Status</span>
              <span className="font-semibold capitalize text-gray-900">{status.replace('-', ' ')}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-200/50">
              <span className="text-sm text-gray-600 font-medium">Priority</span>
              <span className="font-semibold capitalize text-gray-900">{task.priority}</span>
            </div>
            {task.deadline && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200/50">
                <span className="text-sm text-gray-600 font-medium">Deadline</span>
                <span className={`font-semibold ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                  {new Date(task.deadline).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            )}
            {task.createdAt && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200/50">
                <span className="text-sm text-gray-600 font-medium">Created</span>
                <span className="font-medium text-gray-900">
                  {new Date(task.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            )}
            {task.updatedAt && task.updatedAt !== task.createdAt && (
              <div className="flex items-center justify-between py-2 border-b border-gray-200/50">
                <span className="text-sm text-gray-600 font-medium">Last Updated</span>
                <span className="font-medium text-gray-900">
                  {new Date(task.updatedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            )}
            {task.origin && task.origin !== 'flowky' && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600 font-medium">Source</span>
                <span className="font-medium text-gray-900">
                  {task.externalData?.repoFullName ? (
                    <span className="text-right">
                      <span className="block text-xs text-gray-500">{task.externalData.repoFullName}</span>
                      <span className="block">#{task.externalData.issueNumber}</span>
                    </span>
                  ) : (
                    <span className="capitalize">{task.origin}</span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
